import { InfiniteScroll } from '@american-autonomy/ag-ui';

import SearchInput from 'components/SearchInput';
import SelectItemContainer from 'components/SelectItemContainer';
import Panel from 'components/containers/Panel';
import SkeletonText from 'components/loaders/SkeletonText';
import LabelText from 'components/text/LabelText';

import useData from './hooks/useData';
import useTranslation from './hooks/useTranslation';

import { DEBOUNCE_DEFAULT_TIME, TEST_ID } from './constants';
import type { FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

const SelectPanel: FunctionComponent<Props> = ({
    children,
    className = '',
    debounceTime = DEBOUNCE_DEFAULT_TIME,
    hasNextPage,
    isLoading,
    items,
    label,
    labelText,
    noItemsFoundText,
    onFilter,
    onNextPage,
    testId = TEST_ID
}) => {
    const { NO_ITEMS_FOUND } = useTranslation();

    const { displayItems, handleSearchChange, hasNoItems, isListLoading } = useData({
        isLoading,
        items
    });

    const fetchNext = async () => {
        if (!hasNextPage || !onNextPage) {
            return;
        }

        await onNextPage();
    };

    return (
        <Panel className={`${styles.selectPanel} ${className}`} testId={testId}>
            <SearchInput
                className={styles.searchInput}
                debounceTime={debounceTime}
                isLoading={isLoading}
                items={items}
                onFilter={onFilter}
                onFilterResults={handleSearchChange}
                placeholder={label}
            />

            <SelectItemContainer>
                {isListLoading && hasNoItems && <SkeletonText gap='lg' lines={8} />}

                {hasNoItems && !isListLoading && (
                    <LabelText
                        className={styles.labelText}
                        text={noItemsFoundText || NO_ITEMS_FOUND}
                    />
                )}

                {!hasNoItems && (
                    <>
                        <LabelText className={styles.labelText} text={labelText} />

                        <InfiniteScroll
                            className={styles.infiniteScroll}
                            fetchNextPage={fetchNext}
                            hasNextPage={!!hasNextPage}
                            loader={hasNextPage ? <SkeletonText gap='lg' lines={8} /> : null}>
                            {displayItems.map(children)}
                        </InfiniteScroll>
                    </>
                )}
            </SelectItemContainer>
        </Panel>
    );
};

export default SelectPanel;
